import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp
} from 'firebase/firestore'
import { db, auth, functions } from '@/firebase'
import { httpsCallable } from 'firebase/functions'
import { v4 as uuidv4 } from 'uuid'
import { deliverAudit } from '@/utils/auditDelivery'

/** Firestore rejects `undefined` field values — drop them from details maps. */
export function omitUndefinedFields(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const out = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value
  }
  return out
}

class AuditLogger {
  constructor() {
    this.enabled = true
  }

  // District tenancy: entries are stamped with the school the session is
  // working in so reads can be scoped per school (see utils/auditLogScope).
  // Logs written with no active school (district-level operations) stay
  // unstamped and surface under the district reader's "District / system"
  // filter. Dynamic import avoids the schoolStore → auditLogger import cycle;
  // a missing store (pre-app-mount) simply means no stamp.
  async getActiveSchoolId() {
    try {
      const { useSchoolStore } = await import('@/store/schoolStore')
      return useSchoolStore().currentSchoolId || null
    } catch {
      return null
    }
  }

  async record(event, scope = {}) {
    if (!this.enabled || !auth.currentUser) return { recorded: false }
    const ownerUid = auth.currentUser.uid
    const eventId = uuidv4()
    const schoolId = Object.hasOwn(scope, 'schoolId')
      ? scope.schoolId
      : await this.getActiveSchoolId()
    // Freeze data for retries; a later editor change must not alter this event.
    const payload = JSON.parse(
      JSON.stringify(
        omitUndefinedFields({ ...event, eventId, schoolId, sessionId: this.getSessionId() })
      )
    )
    return deliverAudit({
      id: eventId,
      ownerUid,
      send: () => httpsCallable(functions, 'recordAuditEvent')(payload)
    })
  }

  logStudentAccess(studentId, action, details = {}, scope = {}) {
    return this.record(
      { type: 'student_access', studentId, action, details: omitUndefinedFields(details) },
      scope
    )
  }

  logRosterAccess(students) {
    const groups = new Map()
    for (const student of students) {
      if (!student?.id) continue
      const school = student.schoolId || null
      if (!groups.has(school)) groups.set(school, new Set())
      groups.get(school).add(student.id)
    }
    return Promise.all(
      [...groups].flatMap(([schoolId, ids]) => {
        const all = [...ids]
        const work = []
        for (let i = 0; i < all.length; i += 100) {
          const studentIds = all.slice(i, i + 100)
          work.push(
            this.record(
              {
                type: 'student_access',
                action: 'roster_displayed',
                studentIds,
                studentCount: studentIds.length,
                details: { viewType: 'student_table', evidence: 'rendered_rows' }
              },
              { schoolId }
            )
          )
        }
        return work
      })
    )
  }

  logUserManagement(targetUserId, action, details = {}) {
    return this.record({
      type: 'user_management',
      targetUserId,
      action,
      details: omitUndefinedFields(details)
    })
  }

  logSystemAccess(action, details = {}, scope = {}) {
    return this.record(
      { type: 'system_access', action, details: omitUndefinedFields(details) },
      scope
    )
  }

  logDataExport(exportType, studentIds, details = {}, scope = {}) {
    const ids = [
      ...new Set((Array.isArray(studentIds) ? studentIds : [studentIds]).filter(Boolean))
    ]
    // null means unknown. A backup's scope must never masquerade as zero students.
    const studentCount = Number.isInteger(details.studentCount)
      ? details.studentCount
      : ids.length
        ? ids.length
        : details.blocked === true
          ? 0
          : null
    return this.record(
      {
        type: 'data_export',
        exportType,
        studentIds: ids.slice(0, 500),
        studentCount,
        details: omitUndefinedFields({
          ...details,
          ...(ids.length > 500 ? { studentIdsTruncated: true } : {})
        })
      },
      scope
    )
  }

  logDocumentOpen(fileId, studentId, schoolId) {
    return this.record(
      { type: 'document_open', fileId, studentId, details: { evidence: 'browser_open_requested' } },
      { schoolId }
    )
  }

  getSessionId() {
    try {
      if (!sessionStorage.getItem('sessionId')) {
        sessionStorage.setItem(
          'sessionId',
          `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        )
      }
      return sessionStorage.getItem('sessionId')
    } catch {
      // Browsers may disable storage; logging must still work.
      return uuidv4()
    }
  }

  async generateAccessReport(studentId, dateRange) {
    try {
      const startTs = Timestamp.fromDate(new Date(dateRange.start))
      const endTs = Timestamp.fromDate(new Date(dateRange.end))

      const q = query(
        collection(db, 'auditLogs'),
        where('studentId', '==', studentId),
        where('timestamp', '>=', startTs),
        where('timestamp', '<=', endTs),
        orderBy('timestamp', 'desc')
      )
      const snapshot = await getDocs(q)
      const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))

      const uniqueUsersMap = new Map()
      for (const log of logs) {
        if (log.userId && !uniqueUsersMap.has(log.userId)) {
          uniqueUsersMap.set(log.userId, {
            userId: log.userId,
            email: log.userEmail || 'unknown',
            role: log.details?.userRole || 'unknown'
          })
        }
      }

      return {
        studentId,
        accessCount: logs.length,
        uniqueUsers: Array.from(uniqueUsersMap.values()),
        actions: logs,
        timeRange: dateRange
      }
    } catch (error) {
      console.error('Report generation failed:', error)
      return null
    }
  }

  async generateDocumentAccessReport(studentId, dateRange) {
    try {
      const startTs = Timestamp.fromDate(new Date(dateRange.start))
      const endTs = Timestamp.fromDate(new Date(dateRange.end))

      let schoolId = null
      try {
        const studentSnap = await getDoc(doc(db, 'students', studentId))
        schoolId = studentSnap.exists() ? studentSnap.data()?.schoolId || null : null
      } catch {
        /* student get may be denied; fall through to session */
      }
      if (!schoolId) schoolId = await this.getActiveSchoolId()

      const parts = [
        where('studentId', '==', studentId),
        where('accessTime', '>=', startTs),
        where('accessTime', '<=', endTs),
        orderBy('accessTime', 'desc')
      ]
      if (schoolId) parts.unshift(where('schoolId', '==', schoolId))
      const q = query(collection(db, 'pdfAccessLogs'), ...parts)
      const snapshot = await getDocs(q)
      const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))

      const uniqueUsersMap = new Map()
      for (const log of logs) {
        if (log.userId && !uniqueUsersMap.has(log.userId)) {
          uniqueUsersMap.set(log.userId, {
            userId: log.userId,
            email: log.userEmail || 'unknown',
            role: log.userRole || 'unknown'
          })
        }
      }

      return {
        studentId,
        documentAccessCount: logs.length,
        uniqueUsers: Array.from(uniqueUsersMap.values()),
        accesses: logs,
        timeRange: dateRange
      }
    } catch (error) {
      console.error('Document access report generation failed:', error)
      return null
    }
  }
}

export const auditLogger = new AuditLogger()
export default function useAuditLogger() {
  return {
    logStudentAccess: auditLogger.logStudentAccess.bind(auditLogger),
    logUserManagement: auditLogger.logUserManagement.bind(auditLogger),
    logSystemAccess: auditLogger.logSystemAccess.bind(auditLogger),
    logDataExport: auditLogger.logDataExport.bind(auditLogger),
    generateAccessReport: auditLogger.generateAccessReport.bind(auditLogger),
    generateDocumentAccessReport: auditLogger.generateDocumentAccessReport.bind(auditLogger)
  }
}
